import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiQuery } from '../api/hooks';
import type {
  CashSummaryResponse,
  DayBoardResponse,
  MachineAlertResponse,
  StockAlertResponse,
  WorklistSummaryResponse,
} from '../api/types';
import { useApiClient } from '../api/apiContext';
import { useAuthStore } from '../auth/authStore';
import { isReleased } from '../config/release';
import { Icon, type IconName } from '../components/Icon';
import { PageHeader } from '../components/PageHeader';
import { useLocalisedName } from '../i18n/localisedName';
import { HomeTrends } from './insight/HomeTrends';
import { amount } from './catalog/money';
import { clinicDayPart, clinicTime, clinicToday, longDate } from './scheduling/clinicTime';
import { useBranchPicker } from './scheduling/useBranchPicker';

/**
 * The landing screen for a signed-in staff member: today, at this branch.
 *
 * Until the 2026-08 craft pass this was a Milestone-0 placeholder — a build note, the viewer's
 * email, and the clinic switcher — which is what everyone in the clinic saw first, every morning,
 * for four milestones after the screens it was standing in for had landed.
 *
 * What it shows now is composed entirely from endpoints the dashboard was already calling: the day
 * board, the worklists, the cash summary, and the stock and machine alerts. No new API, no new
 * contract, no new business rule. That constraint is deliberate — a landing screen that needs a
 * bespoke endpoint is a landing screen that goes stale the moment anything else changes — and it
 * also means every number here is the same number the screen it links to will show.
 *
 * The composition answers the four questions a clinic asks at nine in the morning, in the order
 * they are asked: how does today look, who is next through the door, what is blocked, and what has
 * the desk taken. Anything that is *not* one of those is not here.
 *
 * Under them, since CR-34, the context today sits in: two weeks back and one ahead, from the same
 * two summaries the Appointments and Sales screens draw their charts from (see HomeTrends). Still
 * no endpoint of its own: every figure there is one those screens show.
 */
export function HomePage() {
  const { t, i18n } = useTranslation();
  const client = useApiClient();
  const localised = useLocalisedName();
  const principal = useAuthStore((state) => state.principal);
  const permissions = useAuthStore((state) => state.permissions);
  const activeClinicId = useAuthStore((state) => state.activeClinicId);
  const selectClinic = useAuthStore((state) => state.selectClinic);
  const { clinicId, branchId, branches, choose } = useBranchPicker();

  const today = clinicToday();
  const enabled = Boolean(clinicId && branchId);

  // Each part of the page waits for the screen it summarises to be released (config/release.ts),
  // so nothing here links to a destination that would answer 404, and nothing hidden is fetched.
  const showDay = permissions.has('APPOINTMENT_READ') && isReleased('dayBoard');
  const showTakings = permissions.has('CASH_SUMMARY_READ') && isReleased('sales');
  const showBlockers = ['dayBoard', 'worklists', 'inventory', 'machines'].some(isReleased);

  const board = useApiQuery<DayBoardResponse>(
    queryKeys.dayBoard(clinicId ?? '', branchId ?? '', today),
    `/clinics/${clinicId}/appointments/board?branchId=${branchId}&on=${today}`,
    { enabled: enabled && showDay },
  );
  const worklists = useApiQuery<WorklistSummaryResponse[]>(
    queryKeys.worklists(clinicId ?? '', branchId ?? ''),
    `/clinics/${clinicId}/worklists?branchId=${branchId}`,
    { enabled: enabled && permissions.has('WORKLIST_READ') && isReleased('worklists') },
  );
  const cash = useApiQuery<CashSummaryResponse>(
    queryKeys.cashSummary(clinicId ?? '', branchId ?? '', today),
    `/clinics/${clinicId}/cash-summary?branchId=${branchId}&day=${today}`,
    { enabled: enabled && showTakings },
  );
  const stock = useApiQuery<StockAlertResponse[]>(
    queryKeys.stockAlerts(clinicId ?? '', branchId ?? ''),
    `/clinics/${clinicId}/inventory/alerts?branchId=${branchId}`,
    { enabled: enabled && permissions.has('INVENTORY_READ') && isReleased('inventory') },
  );
  const machines = useApiQuery<MachineAlertResponse[]>(
    queryKeys.machineAlerts(clinicId ?? '', branchId ?? ''),
    `/clinics/${clinicId}/machines/alerts?branchId=${branchId}`,
    { enabled: enabled && permissions.has('MACHINE_READ') && isReleased('machines') },
  );

  const memberships = principal?.memberships ?? [];
  const activeMembership = memberships.find((membership) => membership.clinicId === activeClinicId);

  const appointments = (board.data?.columns ?? []).flatMap((column) => column.appointments ?? []);
  const counted = countByStatus(appointments);
  const expected = counted.total - counted.cancelled;
  const upNext = appointments
    .filter((item) => item.status === 'BOOKED' || item.status === 'CONFIRMED' || item.status === 'ARRIVED')
    .sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? ''))
    .slice(0, 5);

  const pendingWorklistItems = (worklists.data ?? []).reduce(
    (running, list) => running + (list.pendingItems ?? 0),
    0,
  );
  const absentDoctors = (board.data?.columns ?? []).filter((column) => column.absent);

  /* Everything the desk cannot start the day without dealing with, gathered from four sources into
     one list. Separately these are four screens somebody has to remember to open; together they
     are the answer to "is anything wrong this morning", which is the question actually being
     asked. Only genuinely blocking states are here — a warranty ending in twenty-four days is not
     something to interrupt a morning with, and lives on its own screen. */
  const blocking: Blocker[] = [
    ...absentDoctors.map((column) => ({
      key: `absent-${column.employmentId}`,
      icon: 'staff' as IconName,
      text: t('today.blockers.absent', {
        doctor: column.doctorName ?? '',
        reason: column.absenceReason ?? '',
      }),
      to: '/day-board',
    })),
    ...(pendingWorklistItems > 0
      ? [
          {
            key: 'worklists',
            icon: 'worklists' as IconName,
            text: t('today.blockers.worklist', { count: pendingWorklistItems }),
            to: '/worklists',
          },
        ]
      : []),
    ...(stock.data ?? [])
      .filter((alert) => alert.kind === 'OUT_OF_STOCK' || alert.kind === 'EXPIRED')
      .map((alert) => ({
        key: `stock-${alert.itemId}`,
        icon: 'inventory' as IconName,
        text: t('today.blockers.stock', {
          item: localised.name({ nameEn: alert.itemNameEn, nameAr: alert.itemNameAr }),
          state: t(`inventory.alertKinds.${alert.kind}`, alert.kind ?? ''),
        }),
        to: '/inventory',
      })),
    ...(machines.data ?? [])
      .filter((alert) => alert.kind === 'OUT_OF_SERVICE' || alert.kind === 'SERVICE_OVERDUE')
      .map((alert) => ({
        key: `machine-${alert.machineId}-${alert.kind}`,
        icon: 'machines' as IconName,
        text: t('today.blockers.machine', {
          machine: alert.machineName ?? alert.machineCode ?? '',
          state: t(`machines.alertKinds.${alert.kind}`, alert.kind ?? ''),
        }),
        to: '/machines',
      })),
  ];

  return (
    <>
      <PageHeader
        title={t(`today.greeting.${clinicDayPart()}`, { name: firstNameOf(principal?.displayName) })}
        description={longDate(today, i18n.language)}
        actions={
          branches.length > 1 && branchId ? (
            <label className="field field--inline" style={{ marginBlockEnd: 0 }}>
              <span className="visually-hidden">{t('board.branch')}</span>
              <select
                value={branchId}
                onChange={(event) => choose(event.target.value)}
                style={{ minInlineSize: '11rem' }}
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {localised.name(branch)}
                  </option>
                ))}
              </select>
            </label>
          ) : null
        }
      />

      {/* The day as one reading, divided rather than scattered across four cards. The lead measure
          is the number of people expected, because that is what the desk plans around; everything
          else on the strip is a part of it or a consequence of it. */}
      {showDay ? (
        <section className="measures" aria-label={t('today.dayHeading')}>
          <div className="measure measure--lead">
            <span className="measure__label">{t('today.expected')}</span>
            <span className="measure__value">{expected}</span>
            <span className="measure__note">
              {t('today.doctorsWorking', { count: (board.data?.columns ?? []).length })}
            </span>
          </div>
          <div className="measure">
            <span className="measure__label">{t('today.seen')}</span>
            <span className="measure__value">{counted.completed}</span>
            <span className="measure__note">
              {t('today.ofExpected', { done: counted.completed, total: expected || 0 })}
            </span>
          </div>
          <div className="measure">
            <span className="measure__label">{t('today.inClinic')}</span>
            <span className="measure__value">{counted.arrived + counted.inProgress}</span>
            <span className="measure__note">{t('today.inSession', { count: counted.inProgress })}</span>
          </div>
          {showTakings ? (
            <div className="measure">
              <span className="measure__label">
                {t('today.takings', { currency: cash.data?.grossSold?.currency ?? 'EGP' })}
              </span>
              <span className="measure__value">{amount(cash.data?.grossSold) ?? '—'}</span>
              <span className="measure__note">
                {t('today.sales', { count: cash.data?.saleCount ?? 0 })}
              </span>
            </div>
          ) : (
            <div className={blocking.length > 0 ? 'measure measure--alert' : 'measure'}>
              <span className="measure__label">{t('today.needsAnAnswer')}</span>
              <span className="measure__value">{blocking.length}</span>
              <span className="measure__note">{t('today.needsAnAnswerNote')}</span>
            </div>
          )}
        </section>
      ) : null}

      {/* How far through the day the clinic is, as one bar in segments. A share-of-total on a
          common baseline rather than a pie: the three parts are stages of one day, and the orchid
          ramp reads as an ordered progression because that is what it is. */}
      {showDay && counted.total > 0 ? (
        <section className="panel" style={{ marginBlockStart: 'var(--space-4)' }}>
          <div className="panel__header">
            <div className="panel__heading" style={{ marginBlockEnd: 0 }}>
              <h2>{t('today.progressHeading')}</h2>
            </div>
            <Link className="button button--ghost button--compact" to="/day-board">
              {t('today.openBoard')}
              <Icon name="arrowEnd" size={18} />
            </Link>
          </div>

          <div
            className="meter"
            role="img"
            aria-label={t('today.progressLabel', {
              done: counted.completed,
              here: counted.arrived + counted.inProgress,
              later: counted.upcoming,
            })}
          >
            <span
              className="meter__segment"
              style={{ flex: `${counted.completed} 0 0`, background: 'var(--chart-seq-1)' }}
            />
            <span
              className="meter__segment"
              style={{
                flex: `${counted.arrived + counted.inProgress} 0 0`,
                background: 'var(--chart-seq-2)',
              }}
            />
            <span
              className="meter__segment"
              style={{ flex: `${counted.upcoming} 0 0`, background: 'var(--chart-seq-3)' }}
            />
          </div>

          <div className="meter__legend">
            <span className="meter__item">
              <span className="meter__key" style={{ background: 'var(--chart-seq-1)' }} />
              <strong>{counted.completed}</strong> {t('today.legendDone')}
            </span>
            <span className="meter__item">
              <span className="meter__key" style={{ background: 'var(--chart-seq-2)' }} />
              <strong>{counted.arrived + counted.inProgress}</strong> {t('today.legendHere')}
            </span>
            <span className="meter__item">
              <span className="meter__key" style={{ background: 'var(--chart-seq-3)' }} />
              <strong>{counted.upcoming}</strong> {t('today.legendLater')}
            </span>
          </div>
        </section>
      ) : null}

      <div className="today-grid">
        {/* Who is next through the door. Five rows, because a receptionist looking at a landing
            screen wants the next hour and has the board for the rest of the day. */}
        {showDay ? (
          <section className="panel">
            <div className="panel__header">
              <div className="panel__heading" style={{ marginBlockEnd: 0 }}>
                <h2>{t('today.nextHeading')}</h2>
              </div>
            </div>

            {upNext.length === 0 ? (
              <p className="muted">{t('today.nextEmpty')}</p>
            ) : (
              <ul className="list">
                {upNext.map((appointment) => (
                  <li className="list__row" key={appointment.id}>
                    <span className="card__time">
                      <span
                        className="card__dot"
                        style={
                          appointment.status === 'ARRIVED'
                            ? { background: 'var(--color-orchid)' }
                            : undefined
                        }
                      />
                      {clinicTime(appointment.startsAt)}
                    </span>
                    <span className="list__main">
                      <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                        {appointment.patientName}
                      </span>
                      <span className="list__meta">
                        {localised.name({
                          nameEn: appointment.serviceNameEn,
                          nameAr: appointment.serviceNameAr,
                        })}
                        {appointment.doctorName ? ` · ${appointment.doctorName}` : null}
                      </span>
                    </span>
                    {appointment.status === 'ARRIVED' ? (
                      <span className="pill pill--busy">{t('board.status.ARRIVED')}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {/* What is waiting on an answer. This is the screen's apricot, and the only place it
            appears: nothing else here is blocked. An empty list is stated plainly rather than
            hidden, because "nothing needs you" is the most useful thing this panel can say. */}
        {showBlockers ? (
        <section className="panel">
          <div className="panel__header">
            <div className="panel__heading" style={{ marginBlockEnd: 0 }}>
              <h2>{t('today.blockedHeading')}</h2>
            </div>
            {blocking.length > 0 ? (
              <span className="badge badge--warning">{blocking.length}</span>
            ) : null}
          </div>

          {blocking.length === 0 ? (
            <p className="muted">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <Icon name="check" size={18} />
                {t('today.blockedEmpty')}
              </span>
            </p>
          ) : (
            <ul className="list">
              {blocking.slice(0, 6).map((blocker) => (
                <li className="list__row" key={blocker.key}>
                  <span className="list__main">
                    <span className="list__meta" style={{ color: 'var(--color-apricot-strong)' }}>
                      <Icon name={blocker.icon} size={16} />
                      {blocker.text}
                    </span>
                  </span>
                  <Link className="button button--ghost button--compact" to={blocker.to}>
                    {t('today.resolve')}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
        ) : null}
      </div>

      {clinicId && branchId ? (
        <HomeTrends
          clinicId={clinicId}
          branchId={branchId}
          today={today}
          showAppointments={permissions.has('APPOINTMENT_READ') && isReleased('appointments')}
          showSales={showTakings}
        />
      ) : null}

      {/* A doctor employed by two clinics (DEC-012) starts unbound and must choose. Kept from the
          Milestone-0 screen, because it is real behaviour rather than a placeholder — but it is
          the last thing on the page now rather than the second, since it is something somebody
          does once a week at most. */}
      {memberships.length > 1 ? (
        <section className="section">
          <h2 className="section__title">{t('common.switchClinic')}</h2>
          <div className="stack--row stack">
            {memberships.map((membership) => {
              const current = membership.clinicId === activeClinicId;
              return (
                <button
                  key={membership.clinicId}
                  type="button"
                  className={current ? 'button' : 'button button--ghost'}
                  aria-pressed={current}
                  disabled={current}
                  onClick={() => {
                    if (membership.clinicId) {
                      void selectClinic(client, membership.clinicId);
                    }
                  }}
                >
                  {current ? <Icon name="check" size={18} /> : <Icon name="switch" size={18} />}
                  {membership.clinicName ?? membership.clinicId}
                </button>
              );
            })}
          </div>
          {activeMembership?.role ? (
            <p className="hint" style={{ marginBlockStart: 'var(--space-3)' }}>
              {t('today.signedInAs', {
                email: principal?.email ?? '',
                role: t(`roles.${activeMembership.role}`, activeMembership.role),
              })}
            </p>
          ) : null}
        </section>
      ) : null}
    </>
  );
}

interface Blocker {
  readonly key: string;
  readonly icon: IconName;
  readonly text: string;
  readonly to: string;
}

interface DayCounts {
  readonly total: number;
  readonly completed: number;
  readonly arrived: number;
  readonly inProgress: number;
  readonly cancelled: number;
  readonly upcoming: number;
}

/**
 * The day's appointments reduced to the five numbers the screen shows.
 *
 * `upcoming` deliberately excludes cancellations and no-shows: a day whose bar counted them as
 * "still to come" would never finish, and the desk would be told at six in the evening that four
 * people were still expected.
 */
function countByStatus(appointments: readonly { readonly status?: string }[]): DayCounts {
  let completed = 0;
  let arrived = 0;
  let inProgress = 0;
  let cancelled = 0;
  let upcoming = 0;

  for (const appointment of appointments) {
    switch (appointment.status) {
      case 'COMPLETED':
        completed += 1;
        break;
      case 'ARRIVED':
        arrived += 1;
        break;
      case 'IN_PROGRESS':
        inProgress += 1;
        break;
      case 'CANCELLED':
      case 'NO_SHOW':
        cancelled += 1;
        break;
      default:
        upcoming += 1;
    }
  }

  return { total: appointments.length, completed, arrived, inProgress, cancelled, upcoming };
}

/** "Yasmin", from "Yasmin Abdel Rahman" — a greeting uses one name, not a record. */
function firstNameOf(name: string | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}
